/**
 * AnnotateOverlay (#546, epic #543) — the brightmark point-and-click / text-select capture surface.
 *
 * Renders while annotate mode is active: a subtle page dim (pointer-events:none so it never
 * intercepts the hover/click target resolution) and a fixed instruction toast with a Cancel button. A
 * hover highlighter from `lib/brightmark/hover.ts` outlines the element under the cursor.
 *
 * Tour coordination (#1979): a guided tour renders react-joyride at z-index 10000 with an
 * interactive overlay/spotlight that would otherwise sit ABOVE this surface and intercept target
 * resolution. When `tourActive` is set, the overlay/toast are raised above the tour and a body class
 * `brightmark-capturing` is toggled that makes the joyride overlay + spotlight pointer-events:none,
 * so clicks pass THROUGH to the real page element (including the spotlighted target and the tour
 * tooltip itself). The Esc handler additionally `stopImmediatePropagation`s so cancelling annotate
 * never reaches joyride's own document key handler — the tour stays mounted on its step and resumable.
 *
 * Capture:
 *  - drag-select text  → `buildTextAnnotation(container, range)` (takes precedence over a click)
 *  - click an element  → `buildElementAnnotation(el, evt)`
 * Either way it resolves the real page element via `elementFromPoint` (skipping its own UI, tagged
 * `data-brightmark-ui`, and the hover lib's own `data-brightmark-agent` overlay) and calls
 * `onCaptured(annotation, targetElement)`. The screenshot is captured by the parent, off this element.
 *
 * Teardown: Esc, or the toast Cancel, calls `onCancel()`; the effect cleanup removes every listener,
 * disables the highlighter, and restores the document cursor. Listeners are attached in the capture
 * phase so they fire before page handlers, and the component is portalled to <body>.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { BrightmarkAnnotation } from "../../../lib/brightmark/types";
import type { BrightmarkLib } from "./brightmarkLazy";

const UI_ATTR = "data-brightmark-ui";

interface AnnotateOverlayProps {
  lib: BrightmarkLib;
  onCaptured: (annotation: BrightmarkAnnotation, target: Element) => void;
  onCancel: () => void;
  /**
   * True when a guided tour is running (#1979). Raises the capture surface above
   * the tour, neutralises the joyride overlay's pointer interception for the
   * capture duration, and stops the cancel-Esc from reaching joyride.
   */
  tourActive?: boolean;
}

/** True if `el` is part of the annotate UI (this overlay/toast) — excluded from capture resolution. */
function isOwnUi(el: Element | null): boolean {
  return !!el && el.closest(`[${UI_ATTR}]`) !== null;
}

export function AnnotateOverlay({ lib, onCaptured, onCancel, tourActive = false }: AnnotateOverlayProps) {
  // Keep the latest callbacks without re-running the listener effect (which would re-bind listeners
  // and re-toggle the highlighter on every parent render).
  const onCapturedRef = useRef(onCaptured);
  const onCancelRef = useRef(onCancel);
  onCapturedRef.current = onCaptured;
  onCancelRef.current = onCancel;

  // Tour pointer-coordination (#1979): while capturing during a tour, mark <body>
  // so the injected style neutralises the joyride overlay/spotlight pointer
  // interception (clicks pass through to the real page element + tour tooltip).
  // Removed on teardown so the tour's interactivity is restored.
  useEffect(() => {
    if (!tourActive) return;
    const CLS = "brightmark-capturing";
    document.body.classList.add(CLS);
    return () => document.body.classList.remove(CLS);
  }, [tourActive]);

  useEffect(() => {
    const highlighter = lib.createHoverHighlighter();
    highlighter.enable();

    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = "crosshair";

    // Resolve the real page element under a point, skipping both our UI and the hover overlay.
    const resolveTarget = (x: number, y: number): Element | null => {
      const el = document.elementFromPoint(x, y);
      if (!el || isOwnUi(el) || lib.isAgentUi(el)) return null;
      return el;
    };

    // Was the last pointer gesture a drag (so mouseup should be read as a text selection, not a click)?
    let dragMoved = false;
    let downX = 0;
    let downY = 0;

    const onPointerDown = (e: PointerEvent): void => {
      if (isOwnUi(e.target as Element | null)) return;
      dragMoved = false;
      downX = e.clientX;
      downY = e.clientY;
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (Math.abs(e.clientX - downX) > 4 || Math.abs(e.clientY - downY) > 4) {
        dragMoved = true;
      }
    };

    // Prefer a non-collapsed text selection (drag-select) over an element click.
    const tryTextCapture = (): boolean => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false;
      const text = sel.toString().trim();
      if (!text) return false;
      const range = sel.getRangeAt(0);
      // The common-ancestor container element of the range (its element host).
      const raw = range.commonAncestorContainer;
      const container =
        raw.nodeType === Node.ELEMENT_NODE
          ? (raw as Element)
          : raw.parentElement;
      if (!container || isOwnUi(container) || lib.isAgentUi(container)) return false;
      const annotation = lib.buildTextAnnotation(container, range);
      onCapturedRef.current(annotation, container);
      return true;
    };

    const onClick = (e: MouseEvent): void => {
      if (isOwnUi(e.target as Element | null)) return; // let the Cancel button work
      e.preventDefault();
      e.stopPropagation();
      // A drag that produced a selection → text annotation; otherwise an element click.
      if (dragMoved && tryTextCapture()) return;
      const target = resolveTarget(e.clientX, e.clientY);
      if (!target) return;
      const annotation = lib.buildElementAnnotation(target, e);
      onCapturedRef.current(annotation, target);
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        // During a tour, keep Esc from reaching react-joyride's own document key
        // handler (which would end the tour); this capture-phase listener fires
        // first, so stopping immediate propagation pauses tour key handling for
        // the cancel (#1979). The tour stays on its step and is resumable.
        e.stopPropagation();
        e.stopImmediatePropagation();
        onCancelRef.current();
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointermove", onPointerMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      highlighter.disable();
      document.body.style.cursor = prevCursor;
    };
  }, [lib]);

  return createPortal(
    <>
      {/* Tour coordination (#1979): make the joyride overlay + spotlight pass
          pointer events through so the user can point-select the spotlighted
          target (and the tour tooltip) underneath. Scoped to capture only.
          The real interceptor in react-joyride 3.x is the SVG <path> INSIDE
          .react-joyride__spotlight (inline `pointer-events:auto`), so the
          descendant `path` selector is required -- neutralising only the svg
          leaves the path live and `elementFromPoint` still returns it. The
          .react-joyride__overlay div is already `pointer-events:none`; it is
          kept here belt-and-braces. The tooltip (.react-joyride__floater /
          __tooltip) is deliberately untouched so it stays interactive and is
          itself annotatable (elementFromPoint resolves it as the top element). */}
      {tourActive && (
        <style>{`.brightmark-capturing .react-joyride__overlay,.brightmark-capturing .react-joyride__spotlight,.brightmark-capturing .react-joyride__spotlight path{pointer-events:none !important;}`}</style>
      )}
      {/* Page dim — never intercepts target resolution (pointer-events:none).
          Raised above the tour (z-index 10000) so it dims the tour layer too. */}
      <div
        {...{ [UI_ATTR]: "overlay" }}
        aria-hidden="true"
        className="fixed inset-0 z-[10050] bg-black/20 pointer-events-none"
      />
      {/* Instruction toast — bottom on mobile, top-centre on larger screens.
          Raised above the tour (z-index 10000) so its Cancel stays clickable. */}
      <div
        {...{ [UI_ATTR]: "toast" }}
        role="status"
        className="fixed z-[10060] left-1/2 -translate-x-1/2 bottom-4 sm:bottom-auto sm:top-4 flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-2.5 shadow-xl max-w-[92vw]"
      >
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          Click an element, or select text
        </span>
        <span className="hidden sm:inline text-xs text-[var(--color-text-muted)]">Esc to cancel</span>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] sm:min-h-0 sm:py-1.5 px-3 rounded-md border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)]"
        >
          Cancel
        </button>
      </div>
    </>,
    document.body,
  );
}
